import React from "react";
import { Wizard, Step, SuccessMessage } from "@countysuite/ui";
import { Http } from "utils/Http";
import "../LackOfProsecution.scss";
import {
  LackOfProsecutionTemplateDto,
  CaseCategoryResponse,
  CaseEventCategoryResponse,
  GetDefaultCaseCategoriesResponse,
  GetDefaultExclusionListResponse,
  GetDefaultActivityListResponse,
  DefaultLOPTemplateResponse,
  GetSignaturesResponse,
  DispositionCategoryType,
  GetDefaultInclusionListRepsonse,
  GetDefaultLettersResponse,
} from "../LackOfProsecutionTypes";
import {
  RequestCategorySubCategory,
  CaseEventCategory,
  Official,
  GetDispositionCategoriesResponse,
  DropdownCategory,
  GetLettersRepsonse,
  LackOfProsecutionLetter,
} from "types/CivilTypes";
import { LackOfProsecutionCaseCategoriesPanel } from "../CaseCategoriesPanel/LackOfProsecutionCaseCategoriesPanel";
import { LackOfProsecutionCaseEventCategoriesPanel } from "../CaseEventCategoriesPanel/LackOfProsecutionCaseEventCategoriesPanel";
import { TemplateLettersPanel } from "../TemplateLettersPanel/TemplateLettersPanel";
import { TemplateDetailsPanel } from "./TemplateDetailsPanel/TemplateDetailsPanel";
import { CreateNewTemplateModal } from "./CreateNewTemplateModal/CreateNewTemplateModal";
import shallowCompare from "utils/shallowEqual";

export interface Props {}

enum ModalNames {
  AddTemplate = "Add New Template",
  UnsavedChanges = "Unsaved changes",
}

interface State {
  templateId: number;
  /**current details */
  details: LackOfProsecutionTemplateDto;
  selectedCaseCategoryIDs: string[];
  ignoredDocketIDs: number[];
  excludedDocketIDs: number[];
  includedDocketIDs: number[];
  lettersIDs: number[];

  waitMessage: string;
  saveMessage: string;
  serverErrors: string[];

  caseCategories: RequestCategorySubCategory[];
  caseEventCategories: CaseEventCategory[];
  letters: LackOfProsecutionLetter[];
  signatures: Official[];
  dispositionCategoryTypes: DispositionCategoryType[];

  lopTemplates: LackOfProsecutionTemplateDto[];

  modalName: string;
}

function createTemplateDto(): LackOfProsecutionTemplateDto {
  return {
    id: 0,
    name: "",
    visible: false,
    sequence: 999,
    defaultDaysOfInactivity: 365,
    defaultSignatureOfficialID: 1,
    excludeZeroDollarJudgments: false,
    dispositionCategoryID: 0,
    dispositionTypeID: 0,
    hideDeadlineDate: false,
    referenceDateLabel: "",
    showParticipantListButton: false,
    showLettersSelect: false,
    lettersIDs: [],
    showPretrialDatePicker: false,
    ignoreJudgementCases: false,
  };
}

export class LackOfProsecutionEditTemplatePage extends React.Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = {
      templateId: 0,
      details: createTemplateDto(),
      selectedCaseCategoryIDs: [],
      ignoredDocketIDs: [],
      excludedDocketIDs: [],
      includedDocketIDs: [],
      lettersIDs: [],

      caseCategories: [],
      caseEventCategories: [],
      signatures: [],
      dispositionCategoryTypes: [],

      lopTemplates: [],

      waitMessage: "",
      saveMessage: "",
      serverErrors: [],
      letters: [],

      modalName: "",
    };
  }

  componentDidMount() {
    this.fetchCaseCategories();
    this.fetchCaseEventCategories();
    this.fetchLopTemplates();
    this.fetchSignatures();
    this.fetchDispositionCatgeoryTypes();
    this.fetchLetters();
  }

  fetchLopTemplates = async (
    id?: number
  ): Promise<LackOfProsecutionTemplateDto[]> => {
    const data = { id: id || 0 };
    const res = await Http.get<DefaultLOPTemplateResponse>({
      url: "LackOfProsecutionTemplate/GetDetails",
      data: data,
    });
    const templates = res.templates || [];
    this.setState({ lopTemplates: templates });
    return templates;
  };

  fetchCaseCategories = async () => {
    const res = await Http.get<CaseCategoryResponse>({
      url: "LackOfProsecution/GetCaseCategories",
    });
    this.setState({ caseCategories: res.caseCategories || [] });
  };

  fetchCaseEventCategories = async () => {
    const res = await Http.get<CaseEventCategoryResponse>({
      url: "LackOfProsecution/GetCaseEventCategories",
    });
    this.setState({ caseEventCategories: res.caseEventCategories || [] });
  };

  fetchLetters = async () => {
    const res = await Http.get<GetLettersRepsonse>({
      url: "LackOfProsecution/GetLetters",
    });
    this.setState({ letters: res.letters || [] });
  };

  fetchSelectedCaseCategoryIDs = async (id?: number) => {
    const data = { templateID: id || 0 };
    const res = await Http.get<GetDefaultCaseCategoriesResponse>({
      url: "LackOfProsecutionTemplate/GetDefaultCaseCategories",
      data: data,
    });
    this.setState({
      selectedCaseCategoryIDs: res.selectedCaseCategoryIDs || [],
    });
  };

  fetchSelectedActivityIgnoreListIDs = async (id?: number) => {
    const data = { templateID: id || 0 };
    const res = await Http.get<GetDefaultActivityListResponse>({
      url: "LackOfProsecutionTemplate/GetDefaultActivityIgnoreList",
      data: data,
    });
    this.setState({
      ignoredDocketIDs: res.selectedActivityIgnoreListIDs || [],
    });
  };

  fetchSelectedExclusionListIDs = async (id?: number) => {
    const data = { templateID: id || 0 };
    const res = await Http.get<GetDefaultExclusionListResponse>({
      url: "LackOfProsecutionTemplate/GetDefaultExclusionList",
      data: data,
    });
    this.setState({ excludedDocketIDs: res.selectedExclusionListIDs || [] });
  };

  fetchSelectedInclucdedListIDs = async (id?: number) => {
    const data = { templateID: id || 0 };
    const res = await Http.get<GetDefaultInclusionListRepsonse>({
      url: "LackOfProsecutionTemplate/GetDefaultInclusionList",
      data: data,
    });
    this.setState({ includedDocketIDs: res.selectedInclusionIDs || [] });
  };

  fetchSelectedIncludedLettersIDs = async (id?: number) => {
    const data = { templateID: id || 0 };
    const res = await Http.get<GetDefaultLettersResponse>({
      url: "LackOfProsecutionTemplate/GetDefaultLetters",
      data: data,
    });
    this.setState({ lettersIDs: res.selectedLettersIDs || [] });
  };

  fetchIdSelections = async (id?: number) => {
    if (id && id > 0) {
      this.fetchSelectedCaseCategoryIDs(id);
      this.fetchSelectedExclusionListIDs(id);
      this.fetchSelectedActivityIgnoreListIDs(id);
      this.fetchSelectedInclucdedListIDs(id);
      this.fetchSelectedIncludedLettersIDs(id);
    }
  };

  fetchSignatures = async () => {
    const res = await Http.get<GetSignaturesResponse>({
      url: "LackOfProsecutionTemplate/GetOfficialsForSignatures",
    });
    this.setState({ signatures: res.officials || [] });
  };

  fetchDispositionCatgeoryTypes = async () => {
    const res = await Http.get<GetDispositionCategoriesResponse>({
      url: "LackOfProsecutionTemplate/GetDispositionCategories",
    });
    this.setState({ dispositionCategoryTypes: res.dispositionCategoryTypes });
  };

  changeDetails = (newDetails: LackOfProsecutionTemplateDto) => {
    this.setState((prevState) => {
      const nextDetails = { ...prevState.details, ...newDetails };
      const nextState = { details: nextDetails };
      return nextState;
    });
  };

  handleTemplateAdded = async (newTemplateId: number) => {
    await this.fetchLopTemplates();
    this.setState({ modalName: "" });
    this.handleSelectTemplate(newTemplateId);
  };

  handleTemplateDirtyChecking = (templateId: number) => {
    if (!templateId) return;
    const lt = this.state.lopTemplates.find((lt) => lt.id === templateId);
    if (lt && shallowCompare(this.state.details, lt)) {
      this.setState({ modalName: ModalNames.UnsavedChanges });
    } else {
      this.handleSelectTemplate(templateId);
    }
  };

  handleSelectTemplate = (templateId: number) => {
    const details =
      this.state.lopTemplates.find((lt) => lt.id === templateId) ||
      createTemplateDto();
    this.setState({ templateId, details });
    this.fetchIdSelections(templateId);
  };

  handleCaseCategorySelections = (selections: string[]) => {
    this.setState({ selectedCaseCategoryIDs: selections });
  };

  handleActivityIgnoreListSelections = (selections: number[]) => {
    this.setState({ ignoredDocketIDs: selections });
  };

  handleExclusionListSelections = (selections: number[]) => {
    this.setState({ excludedDocketIDs: selections });
  };

  handleInclusionListSelections = (selections: number[]) => {
    this.setState({ includedDocketIDs: selections });
  };

  handleLetterInclusionsSelections = (selections: number[]) => {
    this.setState({ lettersIDs: selections });
  };

  showAddTemplateModal = () => {
    this.setState({ modalName: ModalNames.AddTemplate });
  };

  saveToServer = async (
    controllerUrl: string,
    data: any
  ): Promise<{ success: boolean; templateID?: number }> => {
    if (!data || !data.templateID) return { success: false };

    this.setState({ waitMessage: "Saving, please wait...", saveMessage: "" });
    try {
      const saveResponse = await Http.post<DefaultLOPTemplateResponse>({
        url: controllerUrl,
        data: data,
      });
      if (saveResponse.success) {
        this.setState({
          saveMessage: saveResponse.message || "Save successful.",
        });
        return { success: true, templateID: saveResponse.templateID };
      } else {
        this.setState({ serverErrors: saveResponse.errors || [] });
        return { success: false, templateID: saveResponse.templateID };
      }
    } catch (err) {
      this.setState({
        serverErrors: [
          "An unexpected error has occurred.  Please reload CountySuite.",
        ],
      });
    } finally {
      this.setState({ waitMessage: "" });
    }

    return { success: false };
  };

  saveTemplate = async () => {
    const url = "LackOfProsecutionTemplate/SaveDetails";
    const data = {
      ...this.state.details,
      templateID: this.state.templateId,
      excludedDocketIDs: this.state.excludedDocketIDs,
      ignoredDocketIDs: this.state.ignoredDocketIDs,
      includedDocketIDs: this.state.includedDocketIDs,
      caseCategoryIDs: this.state.selectedCaseCategoryIDs,
      lettersIDs: this.state.lettersIDs,
    };

    const res = await this.saveToServer(url, data);
    if (res.success && res.templateID) {
      await this.fetchLopTemplates();
      this.handleSelectTemplate(res.templateID);
    }
    return res.success;
  };

  render() {
    const { details } = this.state;

    return (
      <div>
        <SuccessMessage
          message={this.state.saveMessage}
          show={this.state.saveMessage.length > 0}
          onDismiss={() => this.setState({ saveMessage: "" })}
        />
        <CreateNewTemplateModal
          show={this.state.modalName === ModalNames.AddTemplate}
          onHide={() => this.setState({ modalName: "" })}
          onTemplateAdded={this.handleTemplateAdded}
          signatures={this.state.signatures}
          dispositionCategories={this.state.dispositionCategoryTypes}
        />
        <Wizard>
          <Step id={1} name="Template Details">
            {(stepProps) => (
              <TemplateDetailsPanel
                {...details}
                changeDetails={this.changeDetails}
                onSave={this.saveTemplate}
                onSelectTemplate={this.handleSelectTemplate}
                showAddTemplateModal={this.showAddTemplateModal}
                lopTemplates={this.state.lopTemplates}
                signatures={this.state.signatures}
                dispositionCategories={this.state.dispositionCategoryTypes}
              />
            )}
          </Step>
          <Step
            id={2}
            name="Case Categories"
            disabled={this.state.templateId <= 0}
          >
            {(stepProps) => (
              <LackOfProsecutionCaseCategoriesPanel
                caseCategories={this.state.caseCategories}
                selectedIDs={this.state.selectedCaseCategoryIDs}
                onChangeSelectedIDs={this.handleCaseCategorySelections}
                onSave={this.saveTemplate}
                nextStep={stepProps.next}
              >
                <p>
                  Please select the Case Categories (with sub-category, when
                  appropriate) that should be included when searching for
                  applicable Cases, using this template.
                </p>
              </LackOfProsecutionCaseCategoriesPanel>
            )}
          </Step>
          <Step
            id={3}
            name="Ignored Dockets"
            disabled={this.state.templateId <= 0}
          >
            {(stepProps) => (
              <LackOfProsecutionCaseEventCategoriesPanel
                title="Docket Categories that are Ignored"
                categories={this.state.caseEventCategories}
                selectedIDs={this.state.ignoredDocketIDs}
                onChangeSelectedIDs={this.handleActivityIgnoreListSelections}
                onSave={this.saveTemplate}
                nextStep={stepProps.next}
              >
                <p>
                  Please select the Docket Categories that are ignored when
                  determining the date of the most recent activity on a Case.
                </p>
              </LackOfProsecutionCaseEventCategoriesPanel>
            )}
          </Step>
          <Step
            id={4}
            name="Excluded Dockets"
            disabled={this.state.templateId <= 0}
          >
            {(stepProps) => (
              <LackOfProsecutionCaseEventCategoriesPanel
                title="Docket Categories that Exclude Cases"
                categories={this.state.caseEventCategories}
                selectedIDs={this.state.excludedDocketIDs}
                onChangeSelectedIDs={this.handleExclusionListSelections}
                onSave={this.saveTemplate}
                nextStep={stepProps.next}
              >
                <p>
                  Please select the Docket Categories that, if present on a
                  Case, will cause that Case to be excluded when searching for
                  applicable Cases, regardless of whether other criteria are met
                </p>
              </LackOfProsecutionCaseEventCategoriesPanel>
            )}
          </Step>
          <Step
            id={5}
            name="Included Dockets"
            disabled={this.state.templateId <= 0}
          >
            {(stepProps) => (
              <LackOfProsecutionCaseEventCategoriesPanel
                title="Docket Categories that Include Cases"
                categories={this.state.caseEventCategories}
                selectedIDs={this.state.includedDocketIDs}
                onChangeSelectedIDs={this.handleInclusionListSelections}
                onSave={this.saveTemplate}
                nextStep={stepProps.next}
              >
                <p>
                  Please select the Docket Categories that, if present on a
                  Case, will cause a Case to be included when searching for
                  applicable Cases. If no Docket Categories are selected, then
                  only the other criteria determine if a Case should be
                  included. If any Docket Categories are selected, then a Case
                  willy only be selected if those Docket Categories are present
                  on that Case, in addition to other criteria being met
                </p>
              </LackOfProsecutionCaseEventCategoriesPanel>
            )}
          </Step>
          <Step id={6} name="Letters" disabled={this.state.templateId <= 0}>
            {(stepProps) => (
              <TemplateLettersPanel
                title="Letters that are Available to Generate"
                letters={this.state.letters}
                selectedIDs={this.state.lettersIDs}
                onChangeSelectedIDs={this.handleLetterInclusionsSelections}
                onSave={this.saveTemplate}
                nextStep={stepProps.next}
              >
                <p>
                  Please select the Letters that will be used for this template
                </p>
              </TemplateLettersPanel>
            )}
          </Step>
        </Wizard>
      </div>
    );
  }
}
